import React from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent,
  DialogActions, 
  TextField, 
  Box, 
  Button,
  Alert,
  Typography
} from "@mui/material";
import TimelineIcon from '@mui/icons-material/Timeline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TankIcon from '@mui/icons-material/Water';
import axios from "axios";
import { useNavigate } from "react-router-dom";

const MAX_NUMBER = 200000;

// 🚀 サービスモード対応残量更新ダイアログ
const UpdateRemainingDialog = ({ 
  open, 
  onClose, 
  clientProduct, 
  selectedClientProductId,
  onUpdate,
  serviceMode = 'PREDICTION_AUTO' // デフォルト値で下位互換性確保
}) => {
  const navigate = useNavigate();
  const [newRemaining, setNewRemaining] = React.useState("");
  const [newMeasuredDate, setNewMeasuredDate] = React.useState("");
  const [remainingError, setRemainingError] = React.useState("");
  const [dateError, setDateError] = React.useState("");

  // 🚀 予測機能が有効かどうかの判定
  const canUpdateRemaining = serviceMode === 'PREDICTION_ONLY' || serviceMode === 'PREDICTION_AUTO';

  // ダイアログが開かれたときに、現在の値を初期値として設定
  React.useEffect(() => {
    if (open && clientProduct && canUpdateRemaining) {
      setNewRemaining(clientProduct.actualRemaining?.toString() || "");
      setNewMeasuredDate(clientProduct.measuredDate || new Date().toISOString().split('T')[0]);
      // エラーをリセット
      setRemainingError("");
      setDateError("");
    }
  }, [open, clientProduct, canUpdateRemaining]);

  // 実測残量の検証
  const handleRemainingChange = (value) => {
    setNewRemaining(value);
    if (!value || value.trim() === "") {
      setRemainingError("実測残量を入力してください");
    } else if (!/^[0-9]+$/.test(value)) {
      setRemainingError("数字のみ入力できます");
    } else if (clientProduct?.totalCapacity && Number(value) > Number(clientProduct.totalCapacity)) {
      setRemainingError(`容量（${clientProduct.totalCapacity}L）以下で入力してください`);
    } else if (Number(value) > MAX_NUMBER) {
      setRemainingError("20万以下で入力してください");
    } else {
      setRemainingError("");
    }
  };

  // 実測日の検証
  const handleMeasuredDateChange = (value) => {
    setNewMeasuredDate(value);
    const today = new Date().toISOString().split("T")[0];
    if (value > today) {
      setDateError("未来の日付は指定できません");
    } else if (!value) {
      setDateError("測定日を入力してください");
    } else {
      setDateError("");
    }
  };

  const isRemainingInvalid = remainingError || dateError || !newRemaining || !newMeasuredDate;

  // 🚀 修正：残量更新の送信処理 - 完了後はダイアログを閉じるだけ
  const handleSubmit = async () => {
    if (!newRemaining || !newMeasuredDate) {
      alert("実測残量と実測日を正しく入力してください。");
      return;
    }

    const confirm = window.confirm("実測残量または実測日が更新されます。よろしいですか？");
    if (!confirm) return;

    const token = sessionStorage.getItem("token");
    
    try {
      await axios.put(
        `/api/client-products/${selectedClientProductId}/remaining`, 
        {
          actualRemaining: Number(newRemaining),
          measuredDate: newMeasuredDate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      alert("残量を更新しました");
      
      // 🚀 修正：ダイアログを閉じて顧客詳細画面に戻る
      onClose();
      
      // 🚀 修正：データ更新のみ行う（別のダイアログは開かない）
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      if (err.response?.status === 404) {
        alert("この顧客は削除されています。一覧に戻ります。");
        navigate("/clientList");
      } else {
        alert("更新に失敗しました");
      }
    }
  };

  // 🚀 予測機能が無効な場合の代替ダイアログ
  if (!canUpdateRemaining) {
    const getIconAndMessage = () => {
      if (serviceMode === 'PRICING_ONLY') {
        return {
          icon: <MonetizationOnIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />,
          title: '残量更新には予測機能が必要です',
          description: '現在は基本モード（単価管理のみ）をご利用中です。',
          suggestion: '機能設定から予測機能を有効にすると、実測値から残量予測が可能になります。'
        };
      } else if (serviceMode === 'TANK_MANUAL') {
        return {
          icon: <TankIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />,
          title: '残量更新には予測機能が必要です',
          description: '現在はタンク管理モードをご利用中です。',
          suggestion: '機能設定から予測機能を有効にすると、実測値から残量予測が可能になります。'
        };
      }
      return {
        icon: <TimelineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />,
        title: '残量更新には予測機能が必要です',
        description: '予測機能が有効でない場合、残量更新は利用できません。',
        suggestion: '機能設定から予測機能を有効にしてください。'
      };
    };

    const { icon, title, description, suggestion } = getIconAndMessage();

    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          {icon}
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {description}
          </Typography>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>ヒント：</strong><br/>
              {suggestion}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1.5 }}>
          <Button 
            variant="text"
            onClick={onClose}
            color="inherit"
            size="medium"
            sx={{ minWidth: 100 }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // 🚀 通常の残量更新ダイアログ（予測機能有効時）
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon color="success" />
          <Box>
            <Typography variant="h6">実測残量の更新</Typography>
            <Typography variant="caption" color="text.secondary">
              予測機能で残量が自動計算されます
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>自動予測機能</strong><br/>
            実測値を入力すると、消費量に基づいて今後の残量予測が自動更新されます。
          </Typography>
        </Alert>

        <TextField
          label="実測残量 (L)"
          type="text"
          fullWidth
          value={newRemaining}
          onChange={(e) => handleRemainingChange(e.target.value)}
          error={!!remainingError}
          helperText={remainingError || '現在の正確な残量を入力してください'}
          sx={{ mb: 2 }}
          placeholder="例: 500"
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*"
          }}
        />
        
        <TextField
          label="測定日"
          fullWidth
          type="date"
          value={newMeasuredDate}
          onChange={(e) => {
            const chosen = e.target.value;
            const today = new Date().toISOString().split("T")[0];
            if (chosen > today) {
              alert("未来の日付は登録できません。");
              setNewMeasuredDate(today);
              setDateError("");
            } else {
              handleMeasuredDateChange(chosen);
            }
          }}
          error={!!dateError}
          helperText={dateError || '残量を確認した日付'}
          InputProps={{
            inputProps: {
              max: new Date().toISOString().split("T")[0],
            },
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />

        {/* 🚀 容量との関係を表示（設定されている場合） */}
        {clientProduct?.totalCapacity && newRemaining && !remainingError && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>残量割合：</strong>
              {Math.round((Number(newRemaining) / clientProduct.totalCapacity) * 100)}%
              （{newRemaining}L / {clientProduct.totalCapacity}L）
            </Typography>
          </Alert>
        )}
      </DialogContent>
      
      {/* 🚀 統一されたボタンスタイル */}
      <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 1.5 }}>
        <Button
          variant="contained"
          disabled={isRemainingInvalid}
          onClick={handleSubmit}
          size="large"
          fullWidth
          sx={{ order: 1 }}
        >
          更新
        </Button>
        
        <Button 
          variant="text"
          onClick={onClose}
          color="inherit"
          size="medium"
          sx={{ order: 2, minWidth: 100 }}
        >
          キャンセル
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateRemainingDialog;